import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AuthService } from 'src/app/services/auth.service';
import { OnboardingService } from 'src/app/services/onboarding.service';
import { LocationService } from 'src/app/services/location.service';
import {
  Affluence,
  Coverage,
  TeamSize,
  ServiceTime,
  Periodicity,
  MerchantType,
} from 'src/app/models/onboarding.model';
import type { ActivityType } from 'src/app/models/cluster.model';
import {
  AFFLUENCE_LABELS,
  SERVICE_LABELS,
  PERIODICITY_LABELS,
  TEAM_SIZE_LABELS,
  COVERAGE_OPTIONS,
  ACTIVITY_TYPE_CONFIG,
} from 'src/app/models/onboarding.labels';
import { LogService } from '../../services/log.service';
import { TwoFactorCardComponent } from './two-factor-card/two-factor-card.component';
import { AccountCardComponent } from './account-card/account-card.component';

@Component({
  selector: 'app-profile',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    TwoFactorCardComponent,
    AccountCardComponent,
  ],
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.css'],
})
export class ProfileComponent implements OnInit {
  private log = inject(LogService);
  private cdr = inject(ChangeDetectorRef);

  form!: FormGroup;
  loading = true;
  saving = false;
  merchantType: MerchantType | null = null;
  userId: string | null = null;

  // Labels exposés au template
  affluenceLabels = AFFLUENCE_LABELS;
  serviceLabels = SERVICE_LABELS;
  coverageOptions = COVERAGE_OPTIONS;
  periodicityLabels = PERIODICITY_LABELS;
  teamSizeLabels = TEAM_SIZE_LABELS;

  // Options pour les sélecteurs
  teamSizeOptions = [
    { key: 'solo', icon: '👤', ...TEAM_SIZE_LABELS['solo'] },
    { key: '2persons', icon: '👥', ...TEAM_SIZE_LABELS['2persons'] },
    { key: '3plus', icon: '👨‍👩‍👧', ...TEAM_SIZE_LABELS['3plus'] },
    { key: 'multi_stand', icon: '🏪', ...TEAM_SIZE_LABELS['multi_stand'] },
  ];

  affluenceOptions = [
    { key: 'low', ...AFFLUENCE_LABELS['low'] },
    { key: 'medium', ...AFFLUENCE_LABELS['medium'] },
    { key: 'high', ...AFFLUENCE_LABELS['high'] },
  ];

  serviceOptions = [
    { key: 'Petit-déjeuner', label: SERVICE_LABELS['Petit-déjeuner'] },
    { key: 'Déjeuner', label: SERVICE_LABELS['Déjeuner'] },
    { key: 'Dîner', label: SERVICE_LABELS['Dîner'] },
  ];

  activityOptions = Object.keys(ACTIVITY_TYPE_CONFIG).map((key) => ({
    key,
    label: `${ACTIVITY_TYPE_CONFIG[key as ActivityType].icon} ${ACTIVITY_TYPE_CONFIG[key as ActivityType].label}`,
  }));

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private onboardingService: OnboardingService,
    private locationService: LocationService,
    private router: Router,
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.loadUserProfile();
  }

  private initForm(): void {
    this.form = this.fb.group({
      businessName: ['', Validators.required],
      activityType: ['', Validators.required],
      affluence: [''],
      coverage: [''],
      teamSize: [''],
      services: [[]],
      periodicity: [''],
      // Champs itinérant
      routes: [[]],
      schedule: [[]],
      actionRadius: [30, [Validators.min(5), Validators.max(200)]],
    });
  }

  private loadUserProfile(): void {
    this.loading = true;
    this.authService.getMe().subscribe({
      next: (response) => {
        if (response.user) {
          this.merchantType = response.user.merchantType;
          this.userId =
            (response.user as any).id || (response.user as any)._id || null;

          // Pré-remplir les données de base depuis le profil
          this.form.patchValue({
            businessName: response.user.businessName || '',
            activityType: response.user.activityType || '',
            affluence: response.user.affluence || '',
            coverage: response.user.coverage || '',
            teamSize: response.user.teamSize || '',
            services: response.user.services || [],
            periodicity: response.user.periodicity || '',
            routes: response.user.routes || [],
            schedule: response.user.schedule || [],
            actionRadius: (response.user as any).location?.actionRadius ?? 30,
          });

          this.log.debug('✅ Profil chargé:', response.user);
          this.loading = false;
          this.cdr.markForCheck();
        }
      },
      error: (err) => {
        this.log.error('Erreur lors du chargement du profil:', err);
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  private loadOnboardingData(): void {
    // Cette méthode n'est plus nécessaire car on charge tout depuis getMe()
    // Supprimée
  }

  // Méthodes pour la gestion des services
  toggleService(service: ServiceTime): void {
    const services = this.form.value.services || [];
    const index = services.indexOf(service);

    if (index > -1) {
      services.splice(index, 1);
    } else {
      services.push(service);
    }

    this.form.patchValue({ services });
    this.form.markAsDirty();
  }

  isServiceSelected(service: ServiceTime): boolean {
    const services = this.form.value.services || [];
    return services.includes(service);
  }

  selectAffluence(affluence: Affluence): void {
    this.form.patchValue({ affluence });
    this.form.markAsDirty();
  }

  selectCoverage(coverage: Coverage): void {
    this.form.patchValue({ coverage });
    this.form.markAsDirty();
  }

  selectTeamSize(teamSize: TeamSize): void {
    this.form.patchValue({ teamSize });
    this.form.markAsDirty();
  }

  selectPeriodicity(periodicity: Periodicity): void {
    this.form.patchValue({ periodicity });
    this.form.markAsDirty();
  }

  selectActivityType(activityType: string): void {
    this.form.patchValue({ activityType });
    this.form.markAsDirty();
  }

  isFieldSelected(fieldName: string, value: string): boolean {
    return this.form.value[fieldName] === value;
  }

  onSave(): void {
    // Vérifier les champs essentiels
    if (!this.form.value.businessName || !this.form.value.activityType) {
      alert(
        "Veuillez remplir au minimum le nom de l'établissement et le type d'activité.",
      );
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;

    // Préparer le payload selon le type de marchand
    const payload = {
      businessName: this.form.value.businessName,
      activityType: this.form.value.activityType,
      affluence: this.form.value.affluence,
      coverage: this.form.value.coverage,
      teamSize: this.form.value.teamSize,
      services: this.form.value.services,
      periodicity: this.form.value.periodicity,
      ...(this.merchantType === MerchantType.ITINERANT && {
        routes: this.form.value.routes,
        schedule: this.form.value.schedule,
      }),
    };

    // Appel API pour mettre à jour le profil
    this.authService.updateProfile(payload).subscribe({
      next: (response) => {
        // Si marchand itinérant, persister le rayon d'action séparément
        if (this.merchantType === MerchantType.ITINERANT && this.userId) {
          const radius = this.form.value.actionRadius ?? 30;
          this.locationService
            .updateLocationProfile(this.userId, { actionRadius: radius })
            .subscribe({
              next: () => this.log.debug('✅ Rayon sauvegardé:', radius, 'km'),
              error: (e) => this.log.warn('⚠️ Rayon non sauvegardé:', e),
            });
        }
        this.saving = false;
        this.log.debug('✅ Profil mis à jour:', response);
        alert('Profil mis à jour avec succès !');
        this.form.markAsPristine();
        this.router.navigate(['/dashboard']);
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.saving = false;
        this.log.error('❌ Erreur lors de la mise à jour:', err);
        alert('Erreur lors de la mise à jour du profil. Veuillez réessayer.');
        this.cdr.markForCheck();
      },
    });
  }

  goToProducts(): void {
    this.router.navigate(['/products']);
  }

  get isFixedMerchant(): boolean {
    return this.merchantType === MerchantType.FIXED;
  }

  get isItinerantMerchant(): boolean {
    return this.merchantType === MerchantType.ITINERANT;
  }
}
